import React from 'react';
import { Box, BoxProps, Paper, Stack, useTheme } from '@mui/material';
import { useCdnUrl } from '../utils';
import { useNavigate } from 'react-router-dom';

export interface LogoProps extends Omit<BoxProps, 'children'> {
  variant?: 'small' | 'medium' | 'large' | 'xl';
  height?: number | string;
  width?: number | string;
  showText?: boolean;
  textVariant?: 'small' | 'medium' | 'large';
  textColor?: string;
  logoUrl?: string;
  elevation?: number;
}

const LOGO_URL = 'https://images.justbaat.com/static/dooh_logo%20(1).jpeg';
const LOGO_URL_DARK =
  'https://images.justbaat.com/staging/creative/logo_justbaat_dark.png';

const LOGO_VARIANTS = {
  small: {
    height: 24,
    width: 'auto',
    textSize: '0.75rem',
  },
  medium: {
    height: 32,
    width: 'auto',
    textSize: '1rem',
  },
  large: {
    height: 48,
    width: 'auto',
    textSize: '1.25rem',
  },
  xl: {
    height: 64,
    width: 'auto',
    textSize: '1.5rem',
  },
};

export const Logo: React.FC<LogoProps> = ({
  variant = 'medium',
  height,
  width,
  showText = false,
  textVariant,
  textColor = 'primary.main',
  logoUrl,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const resolvedLogoUrl =
    logoUrl || (theme.palette.mode === 'dark' ? LOGO_URL_DARK : LOGO_URL);
  const variantConfig = LOGO_VARIANTS[variant];
  const logoHeight = height || variantConfig.height;
  const logoWidth = width || variantConfig.width;
  const textSize = textVariant
    ? LOGO_VARIANTS[textVariant].textSize
    : variantConfig.textSize;

  // Convert logo URL to CDN URL for optimized delivery
  const cdnLogoUrl = useCdnUrl(resolvedLogoUrl) || resolvedLogoUrl;

  return (
    <Stack
      component={Paper}
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{
        '& img': {
          height: logoHeight,
          width: logoWidth,
          objectFit: 'contain',
        },
        backgroundColor: theme.palette.background.paper,
        padding: '5px 10px',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        ...sx,
      }}
      elevation={1}
      onClick={() => navigate('/app/')}
      {...props}
    >
      <img
        src={cdnLogoUrl}
        alt="JustBaat Logo"
        style={{
          height: logoHeight,
          width: logoWidth,
          objectFit: 'contain',
        }}
      />
      {showText && (
        <Box
          component="span"
          sx={{
            fontSize: textSize,
            fontWeight: 700,
            color: textColor,
            letterSpacing: '-0.02em',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          }}
        >
          JustBaat
        </Box>
      )}
    </Stack>
  );
};

// Convenience components for common use cases
export const LogoSmall: React.FC<Omit<LogoProps, 'variant'>> = props => (
  <Logo variant="small" {...props} />
);

export const LogoMedium: React.FC<Omit<LogoProps, 'variant'>> = props => (
  <Logo variant="medium" {...props} />
);

export const LogoLarge: React.FC<Omit<LogoProps, 'variant'>> = props => (
  <Logo variant="large" elevation={0} {...props} />
);

export const LogoXL: React.FC<Omit<LogoProps, 'variant'>> = props => (
  <Logo variant="xl" {...props} />
);

// Icon-only version (no text)
export const LogoIcon: React.FC<Omit<LogoProps, 'showText'>> = props => (
  <Logo showText={false} {...props} />
);

export default Logo;
